import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import {DEFAULT_PRODUCT_SELECTION} from '../util/constants';

import {BuyBoxEvent, NotificationInterval, NotificationType, ProductSelectionEnum} from '../util/enums';

import {
    BuyBoxNotificationSettings,
    ReviewNotificationSettings,
    StockNotificationSettings
} from '../util/notificationUtil';

import {createNotification, deleteNotification, getNotifications, updateNotification} from '../util/requestSender';

import {ProductSelection} from '../util/types';

import {deepClone} from '../util/utils';

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

interface NotificationProps extends BaseProps {
    notification_type?: NotificationType;
    interval?: NotificationInterval;
    email_addresses?: Array<string>;
    last_sent?: string;
    product_selection?: ProductSelectionEnum;
    product_selection_json?: ProductSelection;
    num_products?: number;
    flex?: any | null;
    account_id?: string;
}

export interface NotificationFields extends BaseFields {
    notification_type: NotificationType;
    interval: NotificationInterval;
    email_addresses: Array<string>;
    last_sent: string;
    product_selection: ProductSelectionEnum;
    product_selection_json: ProductSelection;
    num_products: number;
    flex: any | null;
    account_id: string;
}

const fields: NotificationFields = {
    id: '',
    created_at: '',
    updated_at: '',
    notification_type: NotificationType.REVIEWS,
    interval: NotificationInterval.INSTANT,
    email_addresses: [],
    last_sent: '',
    product_selection: ProductSelectionEnum.ALL,
    product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
    num_products: 0,
    flex: null,
    account_id: ''
};

const fieldsDefinition: {[key in keyof NotificationFields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, null, true),
    notification_type: createFieldDef(ModelFieldType.STRING, NotificationType.REVIEWS),
    last_sent: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    product_selection: createFieldDef(ModelFieldType.STRING, ProductSelectionEnum.ALL),
    product_selection_json: createFieldDef(ModelFieldType.OBJECT, {clauses: [DEFAULT_PRODUCT_SELECTION]}),
    num_products: createFieldDef(ModelFieldType.NUMBER),
    flex: createFieldDef(ModelFieldType.OBJECT, null, true),
    email_addresses: createFieldDef(ModelFieldType.ARRAY),
    interval: createFieldDef(ModelFieldType.STRING, NotificationInterval.INSTANT)
};

const ignoreProps: (keyof NotificationFields)[] = [];

export const FREE_NOTIFICATION_TYPES: Array<NotificationType> = [NotificationType.ORDER];

export default class Notification extends BaseModel<NotificationProps, NotificationFields> {
    productSelectionChanged: boolean;

    static getAll = (accountId: string, callback: ModelResponse<Array<Notification>>) => {
        return getNotifications({
            account_id: accountId,
            callback: (data: ResponseData) => {
                const notifications: Array<Notification> = data.success
                    ? data.notifications.map((noti: any) => new Notification(noti))
                    : [];
                callback(notifications, data.success, data);
            }
        });
    };

    constructor(props?: NotificationProps) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.productSelectionChanged = false;
    }

    get id(): string {
        return this.fields.id as string;
    }

    get notificationType(): NotificationType {
        return this.fields.notification_type;
    }

    get interval(): NotificationInterval {
        return this.fields.interval;
    }

    set interval(interval: NotificationInterval) {
        this.fields.interval = interval;
    }

    get emailAddresses(): Array<string> {
        return this.fields.email_addresses;
    }

    set emailAddresses(email_addresses: Array<string>) {
        this.fields.email_addresses = email_addresses;
    }

    get firstEmailAddress(): string | null {
        if (!this.fields.email_addresses.length) return null;
        return this.fields.email_addresses[0];
    }

    get lastSent(): string {
        return this.fields.last_sent;
    }

    set lastSent(last_sent: string) {
        this.fields.last_sent = last_sent;
    }

    get productSelection(): ProductSelectionEnum {
        return this.fields.product_selection;
    }

    get productSelectionJSON(): ProductSelection {
        return this.fields.product_selection_json;
    }

    set productSelectionJSON(selection: ProductSelection) {
        // TODO: performance — same double-JSON.stringify pattern as in emailStepModel; this setter
        // runs from UI event handlers. Cache the stringified current value or use structural equality.
        if (JSON.stringify(selection) === JSON.stringify(this.fields.product_selection_json)) return;
        this.fields.product_selection_json = selection;
        this.productSelectionChanged = true;
    }

    get numProducts(): number {
        return this.fields.num_products;
    }

    get flex(): any | null {
        return this.fields.flex;
    }

    set flex(flex: any | null) {
        this.fields.flex = flex;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get reviewConfig(): ReviewNotificationSettings {
        return new ReviewNotificationSettings(this.flex);
    }

    get buyBoxConfig(): BuyBoxNotificationSettings {
        return new BuyBoxNotificationSettings(this.flex);
    }

    get stockConfig(): StockNotificationSettings {
        return new StockNotificationSettings(this.flex);
    }

    getFlex = (key: string): any => (this.flex ?? {})[key];

    setFlex = (key: string, value: any) => {
        const flex = this.flex ?? {};
        flex[key] = value;
        this.flex = flex;
    };

    clone = (deep: boolean = true): Notification => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new Notification(fields);
    };

    create = (callback: ModelResponse<Notification>) => {
        return createNotification({
            data: {
                account_id: this.accountId,
                notification_type: this.notificationType,
                interval: this.interval,
                product_selections: this.productSelectionJSON,
                flex: this.flex,
                email_addresses: this.emailAddresses
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.notification);
                callback(this, data.success, data);
            }
        });
    };

    update = (callback?: ModelResponse<Notification>) => {
        return updateNotification({
            data: {
                account_id: this.accountId,
                email_addresses: this.emailAddresses,
                product_selections: this.productSelectionJSON,
                flex: this.flex,
                notification_id: this.id,
                interval: this.interval
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.notification);
                callback?.(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<Notification>) => {
        deleteNotification({
            notification_id: this.id,
            account_id: this.accountId,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: NotificationProps) => {
        this.constructFields(props);
    };
}

export const defaultNotificationValues: Record<NotificationType, Partial<NotificationProps>> = {
    [NotificationType.REVIEWS]: {
        interval: NotificationInterval.DAY,
        product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
        product_selection: ProductSelectionEnum.ALL,
        flex: new ReviewNotificationSettings({ratings: [1, 2, 3, 4, 5]}).dump()
    },
    [NotificationType.PRODUCT_HIJACK]: {
        interval: NotificationInterval.INSTANT,
        product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
        product_selection: ProductSelectionEnum.ALL
    },
    [NotificationType.BUY_BOX]: {
        interval: NotificationInterval.INSTANT,
        product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
        product_selection: ProductSelectionEnum.ALL,
        flex: new BuyBoxNotificationSettings({buy_box_events: [BuyBoxEvent.LOST, BuyBoxEvent.GOT]}).dump()
    },
    [NotificationType.STOCK]: {
        interval: NotificationInterval.INSTANT,
        product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
        product_selection: ProductSelectionEnum.ALL,
        flex: new StockNotificationSettings({threshold: 20}).dump()
    },
    [NotificationType.ORDER]: {
        interval: NotificationInterval.INSTANT,
        product_selection_json: {clauses: [DEFAULT_PRODUCT_SELECTION]},
        product_selection: ProductSelectionEnum.ALL
    }
};

export const freeNotificationTypes: Array<NotificationType> = [NotificationType.ORDER];
